<template>
  <div :class="filtered ? 'container-fluid product-wrapper sidebaron' : 'container-fluid product-wrapper'">
    <div class="product-grid">
      
      <div class="feature-products">
        <BaseFilter
          screenKey="service-prices-list-page"
          :model-value="allParams"
          @update:model-value="val => Object.assign(allParams, val)"
          :fields="filterConfig"
          @search="handleSearch"
          @reset="handleReset"
        />
      </div>
      
      <div class="p-4 bg-white mt-3 shadow-sm rounded">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h3 class="m-0">Service Prices List</h3>
          <div class="table-tools">
             <button class="btn btn-sm btn-outline-secondary" @click="fetchData">
               <i class="fa fa-refresh me-1"></i> Refresh
             </button>
          </div>
        </div>

        <BaseTable
          storage-key="service-prices"
          :columns="tableCols"
          :data="tableData"
          :loading="loading"
          :total-items="total"
          v-model:current-page="allParams.page"
          v-model:page-size="allParams.limit"
          show-checkbox
          @sort="handleSort"
          @selection-change="handleSelection"
        >
          <template #cell(customer_rank_id)="{ item }">
            {{ getCustomerRankName(item.customer_rank_id) }}
          </template>
          <template #cell(service_id)="{ item }">
            {{ getServiceName(item.service_id) }}
          </template>
          <template #cell(price)="{ item }">
            {{ Number(item.price).toLocaleString() }}
          </template>
          <template #cell(created_at)="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
          <template #actions="{ item }">
            <button class="btn btn-sm btn-primary" @click="handleEdit(item)">
              <i class="fa fa-edit"></i>
            </button>
          </template>
        </BaseTable>
      </div>
    </div>
    <ServicePriceModal 
      :isOpen="isShowPriceForm" 
      :itemData="selectedItem"
      :serviceName="selectedServiceName"
      :rankName="selectedRankName"
      @close="isShowPriceForm = false"
      @save-success="fetchData" 
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue';

import BaseFilter from "@/components/common/BaseFilter.vue";
import BaseTable from "@/components/common/BaseTable.vue";
import ServicePriceModal from "./ServicePriceModal.vue";
import { useServicePrices } from "@/composables/useServicePrices";
import { useCustomerRank } from "@/composables/useCustomerRank";
import { useService } from "@/composables/useService";
import { createToaster } from "@meforma/vue-toaster";

const toaster = createToaster({
  position: "top-right",
  duration: 2000,
});
const { fetchServicePricesApi } = useServicePrices();
const filtered = ref(false);
const loading = ref(false);
const tableData = ref([]);
const total = ref(0);

const isShowPriceForm = ref(false);
const selectedItem = ref(null);
const selectedServiceName = ref('');
const selectedRankName = ref('');

const customerRanks = ref([]);
const services = ref([]);
const { getCustomerRanksApi } = useCustomerRank();
const { getServicesApi } = useService();

const allParams = reactive({
  page: 1,
  limit: 10,
  orderBy: 'id',
  sortedBy: 'desc',
  order: '',
  searchJoin: 'AND',
  include: 'manifest',
  searchFields: '',
  search: '',
});

// Cấu hình các ô nhập của Filter
const filterConfig = computed(() => [
  {
    key: 'zone',
    type: 'text',
    label: 'Zone',
    operator: 'like',
    placeholder: 'Zone...',
    isDefault: true
  },
  {
    key: 'customer_rank_id',
    type: 'select',
    operator: '=',
    label: 'Customer Rank',
    options: customerRanks.value.map(item => ({
      label: item.name,
      value: item.id
    })),
    isDefault: true
  },
  {
    key: 'service_id',
    type: 'select',
    operator: '=',
    label: 'Services',
    options: services.value.map(item => ({
      label: item.name,
      value: item.id
    })),
    isDefault: true
  }
]);

// Cấu hình các cột của Bảng
const tableCols = [
  { key: 'id', label: 'ID', width: '70px', sortable: true },
  { key: 'customer_rank_id', label: 'Customer Rank' },
  { key: 'service_id', label: 'Service' },
  { key: 'min_weight', label: 'Min Weight', align: 'center' },
  { key: 'price', label: 'Price', align: 'center' },
  { key: 'currency', label: 'Currency', align: 'center' },
  { key: 'zone', label: 'Zone', align: 'center' },
  { key: 'created_at', label: 'Created At' },
];

const fetchData = async (resetPage = false) => {
  if (resetPage) {
    allParams.page = 1;
  }

  loading.value = true;

  try {
    const res = await fetchServicePricesApi({ ...allParams });

    if (res.success && res.data) {
      tableData.value = res.data.data || [];

      total.value = res.data.meta?.pagination?.total || 0;
    } else {
      toaster.error(res.error || "Không thể tải dữ liệu");
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra trong quá trình kết nối");
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  if (allParams.page === 1) fetchData();
  else allParams.page = 1;
};

const handleEdit = (item) => {
  selectedServiceName.value = getServiceName(item.service_id);
  selectedRankName.value = getCustomerRankName(item.customer_rank_id);
  selectedItem.value = { ...item };
  isShowPriceForm.value = true;
};

const handleReset = () => {
  allParams.search = '';
  allParams.searchFields = '';
  
  if (allParams.page === 1) fetchData();
  else allParams.page = 1;
};

const handleSort = ({ key, order }) => {
  allParams.orderBy = key;
  allParams.order = order;
  fetchData();
};

const handleSelection = (ids) => {
  console.log("IDs đã chọn:", ids);
};

watch(() => [allParams.page, allParams.limit], () => fetchData());

const fetchDependencies = async () => {
  try {
    const [rankRes, serviceRes] = await Promise.all([
      getCustomerRanksApi({ limit: 1000 }),
      getServicesApi({ limit: 1000 })
    ]);
    if (rankRes.success && rankRes.data) {
      customerRanks.value = rankRes.data.data || [];
    }
    if (serviceRes.success && serviceRes.data) {
      services.value = serviceRes.data.data || [];
    }
  } catch (e) {
    console.error(e);
  }
};

const getCustomerRankName = (id) => {
  const rank = customerRanks.value.find(r => r.id === Number(id));
  return rank ? rank.name : id;
};

const getServiceName = (id) => {
  const service = services.value.find(s => s.id === Number(id));
  return service ? service.name : id;
};

onMounted(() => {
  fetchDependencies();
  fetchData();
});

const editUser = (user) => alert('Đang sửa: ' + user.id);
const deleteUser = (user) => {
  if(confirm('Xóa ID ' + user.id + '?')) {
    alert('Đã xóa!');
  }
};
const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);

  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();

  const h = String(date.getHours()).padStart(2, '0');
  const i = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');

  return `${d}/${m}/${y} ${h}:${i}:${s}`;
};
</script>

<style scoped>
.filter-section .left-filter {
  max-height: 400px;
  overflow-y: auto;
  z-index: 99 !important;
}
.product-wrapper {
  padding-bottom: 50px;
}
</style>